import type { ZurichCardServiceCategoriesQuery } from '#build/graphql-operations'
import type { GraphqlResponse } from '#nuxt-graphql-middleware/response'
import { notNullish } from '@vueuse/core'
import type { GraphqlCacheability } from '~/types/cacheability'

type ZurichCardService = {
  name: string
  id: string
  color?: string
  isCover: boolean
  description?: string
  url?: string
  linkTitle?: string
}

function transformResponse(
  data: GraphqlResponse<ZurichCardServiceCategoriesQuery>,
): { services: ZurichCardService[]; cacheability?: GraphqlCacheability } {
  const services =
    data.data.categories.items
      ?.map((v, index) => {
        if (!v) {
          return null
        }

        if (!('description' in v)) {
          return null
        }

        const name = v.name
        const id = v.tid
        const color = v.color?.color
        if (!name || !id) {
          return null
        }
        const isCover = index === 0
        return {
          name: isCover ? 'Zürich<br>Card.' : name,
          isCover,
          id,
          color,
          description: v.description,
          url: v.link?.uri?.path,
          linkTitle: v.link?.title,
        }
      })
      .filter(notNullish) || []

  const cacheability = data.extensions?.cacheability

  return { services, cacheability }
}

export async function useZurichCardServices(): Promise<
  ComputedRef<ZurichCardService[]>
> {
  const language = useCurrentLanguage()

  const { data } = await useCachedAsyncData(
    'zurichCardServices-' + language.value,
    function () {
      return useGraphqlQuery('zurichCardServiceCategories', null, {
        graphqlCaching: {
          client: true,
        },
      })
    },
    {
      transform: transformResponse,
      default: function () {
        return { services: [] }
      },
      deep: false,
      serverMaxAge: 60 * 60 * 24,
      clientMaxAge: 'never',
      serverCacheTags: (data) => data.extensions?.cacheability?.tagsNuxt || [],
      dedupe: 'defer',
      watch: [language],
    },
  )

  return computed(() => data.value.services)
}
